import { FileText, Pencil } from "lucide-react";

import { RiskGauge } from "@/components/contract-intelligence/RiskGauge";
import { Badge, Button, Card, Progress, ScrollArea } from "@/components/ui";
import type { ContractIntelligenceContract, RiskSeverity } from "@/types/contractIntelligence.types";
import { cn } from "@/lib/utils";

interface ContractIntelligenceDashboardProps {
  contract: ContractIntelligenceContract;
  onOpenEditor: () => void;
  onClose: () => void;
}

function parseAmountK(amount: string): number {
  return Number.parseInt(amount.replace(/\D/g, ""), 10) || 0;
}

const severityBorder: Record<RiskSeverity, string> = {
  high: "",
  medium: "",
  low: "",
};

const severityText: Record<RiskSeverity, string> = {
  high: "text-destructive",
  medium: "text-amber-500",
  low: "text-muted-foreground",
};

const severityBar: Record<RiskSeverity, string> = {
  high: "bg-destructive",
  medium: "bg-amber-500",
  low: "bg-muted-foreground",
};

export function ContractIntelligenceDashboard({
  contract,
  onOpenEditor,
  onClose,
}: ContractIntelligenceDashboardProps) {
  const k = contract.keyTerms;
  const paidAmount = k.schedule
    .filter((s) => s.status === "paid")
    .reduce((sum, s) => sum + parseAmountK(s.amount), 0);
  const totalAmount = k.schedule.reduce((sum, s) => sum + parseAmountK(s.amount), 0);
  const paidPct = totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="flex shrink-0 items-center gap-3.5 border-b border-border px-6 py-3.5">
        <div
          className="flex size-[34px] shrink-0 items-center justify-center rounded-lg text-white"
          style={{ background: `linear-gradient(135deg, ${contract.typeColor}, hsl(var(--primary)))` }}
        >
          <FileText className="size-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex items-center gap-2">
            <h1 className="truncate text-sm font-semibold">{contract.name}</h1>
            <Badge variant="outline" style={{ color: contract.typeColor, borderColor: `${contract.typeColor}55` }}>
              {contract.type}
            </Badge>
          </div>
          <p className="text-[11.5px] text-muted-foreground">
            <span className="text-foreground/80">{contract.artist}</span> ↔ {contract.counterparty} · Effective{" "}
            {contract.effectiveDate} · {contract.pages} pages
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            ← New contract
          </Button>
          <Button type="button" size="sm" onClick={onOpenEditor}>
            <Pencil className="size-3.5" aria-hidden="true" />
            Edit with Ari
          </Button>
        </div>
      </header>

      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-4 p-5 pb-8 md:px-6">
          <div className="grid gap-3 lg:grid-cols-[auto_1fr_1fr_1fr_1fr]">
            <Card className="flex items-center gap-3.5 p-4 lg:col-span-1">
              <RiskGauge score={contract.riskScore} />
              <div>
                <p className="text-[11px] tracking-wide text-muted-foreground uppercase">Overall</p>
                <p className="text-sm font-semibold">Push back</p>
                <p className="mt-0.5 max-w-[140px] text-[11px] text-muted-foreground">
                  2 high-risk clauses · 3 to negotiate
                </p>
              </div>
            </Card>
            {[
              { label: "Total Advance", value: `$${contract.advance}K`, sub: "$95K still recoupable", dot: "bg-primary" },
              { label: "Royalty", value: `${contract.royalty}%`, sub: "Net wholesale", dot: "bg-success" },
              { label: "Backend", value: `${contract.backendRoyalty}%`, sub: "Post-recoupment", dot: "bg-amber-500" },
              { label: "Term", value: contract.term, sub: "+ 3 × 1yr options", dot: "bg-violet-500" },
            ].map((stat) => (
              <Card key={stat.label} className="gap-1 p-4">
                <p className="text-[10.5px] tracking-wide text-muted-foreground uppercase">{stat.label}</p>
                <p className="font-mono text-2xl font-semibold tracking-tight">{stat.value}</p>
                <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span className={cn("size-1.5 rounded-full", stat.dot)} aria-hidden="true" />
                  {stat.sub}
                </p>
              </Card>
            ))}
          </div>

          <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr]">
            <Card className="gap-3 p-4 md:p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="text-sm font-semibold">Money you&apos;ll receive</h2>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    ${paidAmount}K of ${totalAmount}K paid · {Math.round(paidPct)}% complete
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]">
                  + Schedule
                </Button>
              </div>
              <Progress value={paidPct} className="h-1.5" />
              <ul className="space-y-2">
                {k.schedule.map((item) => (
                  <li key={item.milestone} className="grid grid-cols-[10px_1fr_auto_auto] items-center gap-2.5">
                    <span
                      className={cn(
                        "size-2 rounded-full",
                        item.status === "paid" ? "bg-success" : "border border-border bg-muted",
                      )}
                      aria-hidden="true"
                    />
                    <span className="text-xs">{item.milestone}</span>
                    <span className="text-[11px] text-muted-foreground">{item.date}</span>
                    <span className="font-mono text-xs font-medium">+{item.amount}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="gap-3 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">Ari&apos;s flags</h2>
                <span className="font-mono text-[10.5px] text-muted-foreground">{k.flags.length} found</span>
              </div>
              <ul className="max-h-[280px] space-y-2 overflow-y-auto">
                {k.flags.map((flag) => (
                  <li
                    key={flag.clause}
                    className={cn("rounded-lg border border-border bg-card p-2.5", severityBorder[flag.severity])}
                  >
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold">{flag.term}</span>
                      <span className={cn("font-mono text-[10px] font-medium", severityText[flag.severity])}>
                        {flag.clause}
                      </span>
                    </div>
                    <p className="text-[11px] leading-relaxed text-muted-foreground">{flag.note}</p>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <Card className="gap-3 p-4 md:p-5">
              <h2 className="text-sm font-semibold">Rights & ownership</h2>
              <ul className="divide-y divide-border">
                {k.rights.map((right) => (
                  <li key={right.name} className="py-2 first:pt-0 last:pb-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium">{right.name}</span>
                      <span className={cn("text-[10.5px] font-semibold tracking-wide uppercase", severityText[right.risk])}>
                        {right.owner}
                      </span>
                    </div>
                    <p className="text-[10.5px] text-muted-foreground">{right.term}</p>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="gap-3 p-4 md:p-5">
              <h2 className="text-sm font-semibold">Obligations</h2>
              <ul className="space-y-2.5">
                {k.obligations.map((obligation) => (
                  <li key={obligation.text} className="flex gap-2.5">
                    <span className={cn("w-0.5 shrink-0 self-stretch rounded-full", severityBar[obligation.risk])} />
                    <div className="min-w-0">
                      <p className="text-[10.5px] font-semibold tracking-wide text-muted-foreground uppercase">
                        {obligation.type}
                      </p>
                      <p className="text-xs leading-relaxed text-muted-foreground">{obligation.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="gap-3 p-4 md:p-5">
              <h2 className="text-sm font-semibold">Key dates & term</h2>
              <ul>
                {k.dates.map((item, index) => (
                  <li key={item.label} className="flex gap-3 pb-2.5">
                    <div className="flex flex-col items-center">
                      <span
                        className={cn(
                          "mt-1 size-2 rounded-full",
                          item.status === "critical" && "bg-destructive shadow-[0_0_8px] shadow-destructive/50",
                          item.status === "upcoming" && "bg-amber-500",
                          item.status === "past" && "bg-muted-foreground/40",
                        )}
                        aria-hidden="true"
                      />
                      {index < k.dates.length - 1 ? <span className="mt-1 w-px flex-1 bg-border" /> : null}
                    </div>
                    <div>
                      <p className="text-[10.5px] font-semibold tracking-wide text-muted-foreground uppercase">
                        {item.label}
                      </p>
                      <p
                        className={cn(
                          "text-[13px] font-medium",
                          item.status === "past" ? "text-muted-foreground" : "text-foreground",
                        )}
                      >
                        {item.date}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
